/**
 * Kundencockpit - verdichtete Management-/Kundensicht (CompanyBrain Welle 3b, BEN-107).
 * Hook fuer den Server-Aggregator /v1/kundencockpit/summary + die KPI-Ampeln aus
 * /v1/metrics/values/latest und den Datenstand aus /v1/cockpit/summary (alle grounded).
 */
import { useQuery } from "@tanstack/react-query";
import { useApi } from "./api";
import { useTenant } from "./tenant";

export type Ampel = "gruen" | "gelb" | "rot" | "grau";

export type GateCell = { phase: string; nr: number | null; status: string | null; ampel: Ampel; kommentar?: string };
export type GateRow = { ressort: string; label: string; cells: GateCell[] };
export type GatePhase = { phase: string; nr: number | null; titel: string };
export type GateRessort = { ressort: string; label: string; tps: string[]; lead?: string | null; spoc?: string | null };
export type Teilprojekt = { ressort: string; label: string; count: number; tps: string[] };

export type PhaseGate = {
  available: boolean;
  reason?: string;
  source: string;
  updated_at?: string | null;
  ressorts: GateRessort[];
  phases: GatePhase[];
  matrix: GateRow[];
  teilprojekte: Teilprojekt[];
  summary: {
    cells_total?: number; in_line?: number; in_verzug?: number;
    nicht_begonnen?: number; progress_pct?: number;
  };
};

export type ProgramAmpel = { ampel: Ampel; label: string; reason: string; progress_pct: number };

export type LastBriefing =
  | { available: true; id?: string; title: string; created_at?: string; audience?: string; excerpt: string; route: string }
  | { available: false; reason: string; cta_route: string };

export type CockpitNextAction = {
  id: string; category: string; priority: string; title: string;
  recommendation: string; route: string; action_label: string;
  intervention: { code: string; name: string; route: string; score: number; warum?: string[] } | null;
};

export type KundencockpitSummary = {
  tenant_id: string;
  tenant_display: string;
  role: "berater" | "kunde";
  generated_at: string;
  degraded: boolean;
  program: ProgramAmpel;
  phase_gate: PhaseGate;
  last_briefing: LastBriefing;
  decisions: { total: number; offen: number; items: { id: string; title: string; route: string }[]; route: string };
  next_steps: { focus_today: string; focus_engine: string; degraded: boolean; actions: CockpitNextAction[] };
  ai_ready: { total: number; abgeschlossen: number; latest: AiReadyLatest | null; route: string };
  deep_links: { label: string; route: string }[];
  _meta?: Record<string, unknown>;
};

export type AiReadyLatest = {
  id: string; title?: string; scope?: string; status?: string;
  overall_score?: number | null; updated_at?: string | null;
};

export type KpiLatest = {
  metric_key?: string;
  name?: string;
  display_name?: string;
  unit: string | null;
  value: number | null;
  target_value: number | null;
  attainment_pct: number | null;
  target_reached: boolean | null;
  ts: string | null;
  source: string | null;
  quality?: string | null;
  value_count?: number;
};

export type CockpitSummaryTrust = {
  generated_at?: string;
  trust_gate?: { status?: string; source_count?: number; trusted_source_count?: number };
};

export function useKundencockpit(role: "berater" | "kunde") {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["kundencockpit", effectiveTenantId, role],
    queryFn: () => api.get<KundencockpitSummary>(`/kundencockpit/summary?role=${role}`),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    staleTime: 30_000,
  });
}

export function useKpiLatest() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["kc-kpis", effectiveTenantId],
    queryFn: () => api.get<KpiLatest[]>("/metrics/values/latest"),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    staleTime: 30_000,
  });
}

export function useCockpitTrust() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["kc-trust", effectiveTenantId],
    queryFn: () => api.get<CockpitSummaryTrust>("/cockpit/summary"),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    staleTime: 30_000,
  });
}

export const AMPEL_LABEL: Record<Ampel, string> = {
  gruen: "im Plan",
  gelb: "im Aufbau",
  rot: "Verzug",
  grau: "offen",
};

/** KPI-Ampel aus Zielerreichung/Zielerreicht (grounded, gleiche Logik wie Steuerkreis). */
export function kpiAmpel(k: KpiLatest): Ampel {
  if (k.value == null || k.attainment_pct == null) return "grau";
  if (k.target_reached) return "gruen";
  if (k.attainment_pct >= 80) return "gelb";
  return "rot";
}
